import {
  HomeIcon,
  BookIcon,
  PipelineIcon,
  ServiceIcon,
  DollarIcon,
  UsersIcon,
  BuildingIcon,
  GiftIcon,
  SparkleIcon
} from "../ui";
import { FEATURES } from "../config/features";

// Commissions is a live page once FEATURES.commissions is on; until then it
// carries a `phase` and renders the designed placeholder (Phase 3, R10). The
// spread keeps both tier rails in sync from one switch.
const COMMISSIONS_PHASE = FEATURES.commissions
  ? {}
  : { phase: "Phase 3", legacyLabel: "Totals live in the legacy dashboard for now" };

/**
 * Single source of truth for the /app shell's navigation (§2.2/§2.3 +
 * REFERRAL-UX-SPEC §0): the sidebar, the ⌘K palette, and the route table
 * all read from here. Navigation is TIER-aware (ROLE-DESIGN-2026-07-11):
 * referral agents get the calm five-item rail around their book; staff
 * (writing/admin) get the workspace rail; admins add the Admin section.
 *
 * `phase` marks entries whose real workspace lands in a later redesign
 * phase — those routes render a designed placeholder pointing at the
 * legacy surface. No `phase` → the page is live.
 */

export const STAFF_PAGES = [
  {
    path: "/app/today",
    label: "Today",
    icon: HomeIcon,
    description:
      "Your morning queue: renewals in the next 60 days, open service requests, reach-outs, and book-at-a-glance stats."
  },
  {
    path: "/app/book",
    label: "Book of Business",
    icon: BookIcon,
    description:
      "One household-grouped client table with real search and filters, leading to the client hub."
  },
  {
    path: "/app/pipeline",
    label: "Pipeline",
    icon: PipelineIcon,
    description:
      "Every prospect by stage — list and board views across all referring agents, with aging."
  },
  {
    path: "/app/service",
    label: "Service",
    icon: ServiceIcon,
    description: "The service-request queue: open and resolved, oldest first, nothing dropped."
  },
  {
    path: "/app/commissions",
    label: "Commissions",
    icon: DollarIcon,
    ...COMMISSIONS_PHASE,
    description:
      "Written premium, policy counts, estimated commissions and splits, filterable by agent and line."
  },
  // Custodian Data-Fix queue (§10.7b). Net-new surface — appears only when
  // FEATURES.dataFixes is on (no `phase` placeholder, no legacy equivalent).
  ...(FEATURES.dataFixes
    ? [
        {
          path: "/app/datafixes",
          label: "Data fixes",
          icon: SparkleIcon,
          description:
            "The Custodian's weekly sweep: proposed fixes for duplicates, premiums, dates, and unlinked records — nothing changes until you say so."
        }
      ]
    : [])
];

/* E §0 — the referral rail: Home, My book, My referrals, Requests,
   Commissions. Resources stays out until it has real content (R12 is
   content work; no dead items). */
export const REFERRAL_PAGES = [
  {
    path: "/app/home",
    label: "Home",
    icon: HomeIcon,
    description: "Your book at a glance, anyone worth a hello, and what's renewing."
  },
  {
    path: "/app/book",
    label: "My book",
    icon: BookIcon,
    description: "The households you've referred — policies, premiums, and your splits."
  },
  {
    path: "/app/pipeline",
    label: "My referrals",
    icon: PipelineIcon,
    description: "Where each referral stands, plus past prospects worth revisiting."
  },
  {
    path: "/app/requests",
    label: "Requests",
    icon: ServiceIcon,
    description: "File a service request for one of your clients and follow its status."
  },
  {
    path: "/app/commissions",
    label: "Commissions",
    icon: DollarIcon,
    ...COMMISSIONS_PHASE,
    description: "Per-policy split lines and monthly rollups, matching what you're paid."
  }
];

export const ADMIN_PAGES = [
  {
    path: "/app/admin/agents",
    label: "Agents",
    icon: UsersIcon,
    phase: "Phase 5",
    description:
      "Agent list and detail with licenses, book stats, tier picker, and an email invite flow replacing password-setting forms.",
    legacyLabel: "Agents live in the legacy dashboard for now"
  },
  {
    path: "/app/admin/carriers",
    label: "Carriers",
    icon: BuildingIcon,
    phase: "Phase 5",
    description:
      "Carrier cards with service and claims contacts and portal links, feeding policy drawers and claims flows.",
    legacyLabel: "Carriers live in the legacy dashboard for now"
  },
  {
    path: "/app/admin/referrals",
    label: "Referrals",
    icon: GiftIcon,
    phase: "Phase 5",
    description:
      "The referral-submissions queue from the public $40 program, with one-click prospect creation.",
    legacyLabel: "Referrals live in the legacy dashboard for now"
  }
];

export function pagesForTier(tier) {
  if (tier === "referral") {
    return REFERRAL_PAGES;
  }
  if (tier === "admin") {
    return [...STAFF_PAGES, ...ADMIN_PAGES];
  }
  return STAFF_PAGES;
}

/** Where the shell lands per tier (/app index). */
export function shellHome(tier) {
  return tier === "referral" ? "/app/home" : "/app/today";
}
